import { cn } from "@/lib/utils";

interface AllisonOrbProps {
  size?: number;
  /** When true, show animated thinking/typing state. */
  active?: boolean;
  className?: string;
}

/**
 * Allison's signature visual — a cyan glowing orb with orbiting rings.
 * This is the *one* element in the product that should always feel alive.
 * Other AI assistants get a generic robot icon; Allison gets a presence.
 */
export function AllisonOrb({
  size = 32,
  active = false,
  className,
}: AllisonOrbProps) {
  return (
    <span
      className={cn(
        "relative inline-flex shrink-0 items-center justify-center",
        className
      )}
      style={{ width: size, height: size }}
      aria-label="Allison"
    >
      {/* Outer halo */}
      <span
        className={cn(
          "absolute inset-0 rounded-full bg-brand/20 blur-md",
          active && "animate-pulse-slow"
        )}
      />

      {/* Orbiting ring 1 */}
      <span
        className={cn(
          "absolute rounded-full border border-brand/40",
          active && "animate-orbit"
        )}
        style={{
          width: size * 0.92,
          height: size * 0.5,
        }}
      />
      {/* Orbiting ring 2, perpendicular */}
      <span
        className={cn(
          "absolute rounded-full border border-brand/30",
          active && "animate-orbit"
        )}
        style={{
          width: size * 0.5,
          height: size * 0.92,
          animationDirection: active ? "reverse" : undefined,
        }}
      />

      {/* Core orb */}
      <span
        className={cn(
          "relative inline-block rounded-full",
          "bg-gradient-to-br from-white via-brand to-brand",
          "allison-glow",
          active && "animate-float"
        )}
        style={{
          width: size * 0.5,
          height: size * 0.5,
        }}
      />
    </span>
  );
}
